import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import { ToastContainer } from "react-toastify";
import { Auth0Provider } from "@auth0/auth0-react";

// Screens & Components
import Dashboard from "./Pages/Dashboard/Dashboard";
import NewDashboard from "./Pages/Dashboard/NewDashboard";
import DashboardContributeNow from "./Pages/Dashboard/DashboardContributeNow";
import DashboardCollabRequest from "./Pages/Dashboard/DashboardCollabRequest";
import DetailsView from "./Pages/DetailsView/DetailsView";
import PaymentSuccess from "./Pages/Payment/PaymentSuccess";
import PaymentFailed from "./Pages/Payment/PaymentFailed";
import Contactus from "./Pages/Contactus/Contactus";
import Favorites from "./Pages/Favorites/Favorites";
import CreateAthleteProfile from "./Pages/CreateAthleteProfile/CreateAthleteProfile";
import FanExperience from "./Pages/FanExperience/FanExperience";
import BrandPartnership from "./Pages/BrandPartnership/BrandPartnership";
import FanEngagementType from "./Pages/FanEngagementType/FanEngagementType";
import BrandPartnershipType from "./Components/BrandPartnershipType/BrandPartnershipType";
import AthleteProfile from "./Pages/AthleteProfile/AthleteProfile";
import AthletePage from "./Pages/Athlete/AthletePage";
import SportsIndex from "./Pages/Catalogue/SportsIndex";
import SportPage from "./Pages/Catalogue/SportPage";
import TeamPage from "./Pages/Catalogue/TeamPage";
import NotFound from "./Pages/NotFound/NotFound";

// Info Pages
import AboutUsComponent from "./Components/Informations/AboutUsComponent/AboutUsComponent";
import Transparency from "./Components/Informations/Transparency/Transparency";
import PrivacyPolicy from "./Components/Informations/PrivacyPolicy/PrivacyPolicy";
import ImpactAthleteJourney from "./Components/Informations/ImpactAthleteJourney/ImpactAthleteJourney";
import NILComplianceAndProcedures from "./Components/Informations/NILComplianceAndProcedures/NILComplianceAndProcedures";
import AddressingNilInequality from "./Components/Informations/AddressingNilInequality/AddressingNilInequality";
import OurMission from "./Components/Informations/OurMission/OurMission";
import NILSupportServicesAndFees from "./Components/Informations/NILSupportServicesAndFees/NILSupportServicesAndFees";

// Admin Screens
import AccountInformation from "./Pages/AdminScreens/AccountInformation/AccountInformation";
import DeleteScreen from "./Pages/AdminScreens/DeleteScreen/DeleteScreen";
import AdminDashboard from "./Pages/AdminScreens/AdminDashboard/AdminDashboard";

// Athlete Screen
import AthleteDashboard from "./Pages/AthleteDashboard/AthleteDashboard";

// Constants & ProtectedRoute
import {
  IMPACT_ATHELETE_JOURNEY,
  ABOUT_US,
  ACCOUNT_INFORMATION,
  ADMIN_DASHBOARD,
  CONTACT_US,
  DASHBOARD,
  DELETE_SCREEN,
  ADDRESSING_NIL_INEQUALITY,
  NIL_SUPPORT_SERVICES_AND_FEES,
  TRANSPARENCY,
  FAVORITES,
  BUILDING_TRUST_IN_NIL,
  NIL_COMPLIANCE_AND_PROCEDURES,
  PAYMENT_FAILED,
  PAYMENT_SUCCESS,
  PRIVACY_POLICY,
  FAN_EXPERIENCE_BROWSE,
  BRAND_PARTNERSHIP_BROWSE,
  ATHLETE_BROWSE,
} from "./Utils/Constants";

import ProtectedRoute from "./Utils/ProtectedRoute";

/**
 * Auth0Provider has to live inside the Router so that onRedirectCallback can use
 * the router to return the user where they started, instead of a full page load.
 */
function Auth0ProviderWithNavigate({ children }) {
  const navigate = useNavigate();

  const onRedirectCallback = (appState) => {
    navigate(appState?.returnTo || DASHBOARD);
  };

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENTID}
      authorizationParams={{ redirect_uri: window.location.origin }}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}

function App() {
  return (
    <Router>
      <Auth0ProviderWithNavigate>
        <div className="App">
          <ToastContainer
            position="top-center"
            autoClose={1000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
          <Routes>
            {/* Landing page */}
            <Route path={DASHBOARD} element={<NewDashboard />} />

            {/* Canonical browse */}
            <Route path={ATHLETE_BROWSE} element={<Dashboard />} />

            {/* Canonical athlete permalink. The id resolves the athlete and the
                slug is decoration, so the link survives a rename AND a
                transfer — the old /DetailsView/:playerId/:teamId shape 404'd
                as soon as an athlete changed schools. */}
            <Route path="/p/:playerParam" element={<AthletePage />} />

            {/* Sport and team pages — neither existed before */}
            <Route path="/sports" element={<SportsIndex />} />
            <Route path="/sports/:sportSlug" element={<SportPage />} />
            <Route path="/sports/:sportSlug/:teamSlug" element={<TeamPage />} />

            {/* Legacy funnel routes. Kept because they are in the wild (QR
                codes, the Auth0 config, shared links) and still work. */}
            <Route
              path={FAN_EXPERIENCE_BROWSE}
              element={<DashboardContributeNow />}
            />
            <Route
              path={`${FAN_EXPERIENCE_BROWSE}/:sportId/:teamId`}
              element={<DashboardContributeNow />}
            />
            <Route
              path={BRAND_PARTNERSHIP_BROWSE}
              element={<DashboardCollabRequest />}
            />
            <Route
              path={`${BRAND_PARTNERSHIP_BROWSE}/:sportId/:teamId`}
              element={<DashboardCollabRequest />}
            />

            {/* The bare `/:teamId` and `/:teamId/:playerId` catch-alls are
                deliberately gone. A single-segment wildcard meant
                nil-collab.com/typo rendered a broken dashboard that fired a
                failing API call, instead of a 404. */}

            {/* Details / engagement pages */}
            <Route
              path="/DetailsView/:playerId/:teamId"
              element={<DetailsView />}
            />
            <Route
              path="/FanExperience/:playerId/:teamId"
              element={<FanExperience />}
            />
            <Route
              path="/BrandPartnership/:playerId/:teamId"
              element={<BrandPartnership />}
            />
            <Route path="/FanEngagementType" element={<FanEngagementType />} />
            <Route
              path="/BrandPartnershipType"
              element={<BrandPartnershipType />}
            />
            <Route path="/AthleteProfile" element={<AthleteProfile />} />

            {/* Contact & Favorites */}
            <Route path={CONTACT_US} element={<Contactus />} />
            <Route path={FAVORITES} element={<Favorites />} />

            {/* Payments */}
            <Route path={PAYMENT_SUCCESS} element={<PaymentSuccess />} />
            <Route path={PAYMENT_FAILED} element={<PaymentFailed />} />

            {/* Create Athlete Profile */}
            <Route
              path="/CreateAthleteProfile"
              element={<CreateAthleteProfile />}
            />

            {/* Info Pages */}
            <Route path={ABOUT_US} element={<AboutUsComponent />} />
            <Route path={TRANSPARENCY} element={<Transparency />} />
            <Route path={PRIVACY_POLICY} element={<PrivacyPolicy />} />
            <Route
              path={IMPACT_ATHELETE_JOURNEY}
              element={<ImpactAthleteJourney />}
            />
            <Route
              path={NIL_COMPLIANCE_AND_PROCEDURES}
              element={<NILComplianceAndProcedures />}
            />
            <Route
              path={ADDRESSING_NIL_INEQUALITY}
              element={<AddressingNilInequality />}
            />
            <Route path={BUILDING_TRUST_IN_NIL} element={<OurMission />} />
            <Route
              path={NIL_SUPPORT_SERVICES_AND_FEES}
              element={<NILSupportServicesAndFees />}
            />

            {/* Account / Protected */}
            <Route
              path={ACCOUNT_INFORMATION}
              element={<AccountInformation />}
            />
            <Route path={DELETE_SCREEN} element={<DeleteScreen />} />

            <Route
              path={ADMIN_DASHBOARD}
              element={
                <ProtectedRoute allowedRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/AthleteDashboard"
              element={
                <ProtectedRoute allowedRole="athlete">
                  <AthleteDashboard />
                </ProtectedRoute>
              }
            />

            {/* There was no 404 route at all. */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </div>
      </Auth0ProviderWithNavigate>
    </Router>
  );
}

export default App;
